import { AdMob, BannerAdSize, BannerAdPosition, BannerAdPluginEvents, RewardAdPluginEvents } from '@capacitor-community/admob';
import { Capacitor } from '@capacitor/core';
import { isDebugMode } from '../utils/debugUtils';
import { debugLog, debugError, debugWarn, debugTable, debugGroup, debugGroupEnd } from '../utils/debugUtils';

/**
 * AdMob Service for managing advertisements in the app
 */
class AdMobService {
    constructor() {
        this.initialized = false;
        this.platform = Capacitor.getPlatform();
        // Check if ads are disabled via environment variable
        this.adsDisabled = process.env.REACT_APP_DISABLE_ADS === 'true';
        // Track if rewarded ad is ready to show
        this.rewardedReady = false;
        this.rewardedLoadListener = null;
        this.rewardedFailListener = null;
    }

    /**
     * Check if ads are disabled
     */
    isAdsDisabled() {
        return this.adsDisabled;
    }

    /**
     * Get the appropriate Ad Unit ID based on platform
     */
    getAdUnitId(type) {
        // No ads on web platform
        if (this.platform === 'web') {
            return null;
        }

        // For native platforms, return AdMob IDs
        const isAndroid = this.platform === 'android';

        switch (type) {
            case 'banner':
                return isAndroid
                    ? process.env.REACT_APP_ADMOB_BANNER_ID_ANDROID
                    : process.env.REACT_APP_ADMOB_BANNER_ID_IOS;
            case 'rewarded':
                return isAndroid
                    ? process.env.REACT_APP_ADMOB_REWARDED_ID_ANDROID
                    : process.env.REACT_APP_ADMOB_REWARDED_ID_IOS;
            default:
                return null;
        }
    }

    /**
     * Initialize AdMob
     */
    async initialize() {
        if (this.initialized) {
            debugLog('AdMob already initialized');
            return;
        }

        // Only initialize on native platforms
        if (this.platform === 'web') {
            debugLog('AdMob not available on web platform');
            return;
        }

        try {
            await AdMob.initialize({
                initializeForTesting: isDebugMode(),
            });

            this.initialized = true;
            debugLog('AdMob initialized successfully');
        } catch (error) {
            console.error('Failed to initialize AdMob:', error);
        }
    }

    /**
     * Show a banner ad
     */
    async showBanner(position = BannerAdPosition.BOTTOM_CENTER) {
        if (!this.initialized) {
            await this.initialize();
        }

        if (this.platform === 'web') {
            debugLog('Banner ads not available on web');
            return;
        }

        try {
            const adId = this.getAdUnitId('banner');

            await AdMob.showBanner({
                adId,
                adSize: BannerAdSize.ADAPTIVE_BANNER,
                position,
                margin: 0,
            });

            debugLog('Banner ad shown');
        } catch (error) {
            console.error('Failed to show banner ad:', error);
        }
    }

    /**
     * Hide the banner ad
     */
    async hideBanner() {
        if (this.platform === 'web') return;

        try {
            await AdMob.hideBanner();
            debugLog('Banner ad hidden');
        } catch (error) {
            console.error('Failed to hide banner ad:', error);
        }
    }

    /**
     * Resume the banner ad
     */
    async resumeBanner() {
        if (this.platform === 'web') return;

        try {
            await AdMob.resumeBanner();
            debugLog('Banner ad resumed');
        } catch (error) {
            console.error('Failed to resume banner ad:', error);
        }
    }

    /**
     * Remove the banner ad
     */
    async removeBanner() {
        if (this.platform === 'web') return;

        try {
            await AdMob.removeBanner();
            debugLog('Banner ad removed');
        } catch (error) {
            console.error('Failed to remove banner ad:', error);
        }
    }

    /**
     * Check if rewarded ad is ready to show
     */
    isRewardedReady() {
        return this.rewardedReady;
    }

    /**
     * Prepare a rewarded ad
     * Returns a promise that resolves when the ad is loaded or rejects on failure
     */
    async prepareRewarded() {
        // Skip if ads are disabled
        if (this.adsDisabled) {
            debugLog('Ads disabled via REACT_APP_DISABLE_ADS');
            return Promise.resolve(false);
        }

        if (!this.initialized) {
            await this.initialize();
        }

        if (this.platform === 'web') {
            debugLog('Rewarded ads not available on web');
            return Promise.resolve(false);
        }

        // Reset ready state
        this.rewardedReady = false;

        // Remove previous listeners if any
        if (this.rewardedLoadListener) {
            this.rewardedLoadListener.remove();
            this.rewardedLoadListener = null;
        }
        if (this.rewardedFailListener) {
            this.rewardedFailListener.remove();
            this.rewardedFailListener = null;
        }

        return new Promise((resolve) => {
            // Listen for load success
            this.rewardedLoadListener = AdMob.addListener(
                RewardAdPluginEvents.Loaded,
                () => {
                    debugLog('Rewarded ad loaded and ready');
                    this.rewardedReady = true;
                    resolve(true);
                }
            );

            // Listen for load failure
            this.rewardedFailListener = AdMob.addListener(
                RewardAdPluginEvents.FailedToLoad,
                (error) => {
                    console.error('Rewarded ad failed to load:', error);
                    this.rewardedReady = false;
                    resolve(false);
                }
            );

            // Start loading the ad
            const adId = this.getAdUnitId('rewarded');

            AdMob.prepareRewardVideoAd({
                adId,
                isTesting: isDebugMode(),
            })
                .then(() => {
                    debugLog('Rewarded ad preparation started');
                })
                .catch((error) => {
                    console.error('Failed to prepare rewarded ad:', error);
                    this.rewardedReady = false;
                    resolve(false);
                });
        });
    }

    /**
     * Show a rewarded ad
     * @param {number} timeout - Max time in ms to wait for the ad to show (default: 5000)
     * @returns {Promise<boolean>} - Resolves to true if ad was shown, false otherwise
     */
    async showRewarded(timeout = 5000) {
        // Skip if ads are disabled
        if (this.adsDisabled) {
            debugLog('Ads disabled via REACT_APP_DISABLE_ADS');
            return false;
        }

        if (this.platform === 'web') {
            debugLog('Rewarded ads not available on web');
            return false;
        }

        // Check if ad is ready
        if (!this.rewardedReady) {
            debugLog('Rewarded ad not ready, skipping');
            return false;
        }

        return new Promise((resolve) => {
            let resolved = false;

            // Set up timeout
            const timeoutId = setTimeout(() => {
                if (!resolved) {
                    resolved = true;
                    debugLog('Rewarded ad show timed out');
                    resolve(false);
                }
            }, timeout);

            // Listen for showed event
            const showedListener = AdMob.addListener(
                RewardAdPluginEvents.Showed,
                () => {
                    if (!resolved) {
                        resolved = true;
                        clearTimeout(timeoutId);
                        debugLog('Rewarded ad shown successfully');
                        this.rewardedReady = false; // Reset ready state after showing
                        showedListener.remove();
                        resolve(true);
                    }
                }
            );

            // Listen for failed to show
            const failedListener = AdMob.addListener(
                RewardAdPluginEvents.FailedToShow,
                () => {
                    if (!resolved) {
                        resolved = true;
                        clearTimeout(timeoutId);
                        debugLog('Rewarded ad failed to show');
                        this.rewardedReady = false;
                        failedListener.remove();
                        resolve(false);
                    }
                }
            );

            // Try to show the ad
            AdMob.showRewardVideoAd()
                .then(() => {
                    debugLog('Rewarded ad show requested');
                })
                .catch((error) => {
                    if (!resolved) {
                        resolved = true;
                        clearTimeout(timeoutId);
                        console.error('Failed to show rewarded ad:', error);
                        this.rewardedReady = false;
                        showedListener.remove();
                        failedListener.remove();
                        resolve(false);
                    }
                });
        });
    }

    /**
     * Add event listeners for ad events
     */
    addBannerListeners(callbacks = {}) {
        if (this.platform === 'web') return { remove: () => { } };

        const listeners = [];

        if (callbacks.onLoaded) {
            listeners.push(
                AdMob.addListener(BannerAdPluginEvents.Loaded, callbacks.onLoaded)
            );
        }
        if (callbacks.onFailed) {
            listeners.push(
                AdMob.addListener(BannerAdPluginEvents.FailedToLoad, callbacks.onFailed)
            );
        }
        if (callbacks.onOpened) {
            listeners.push(
                AdMob.addListener(BannerAdPluginEvents.Opened, callbacks.onOpened)
            );
        }
        if (callbacks.onClosed) {
            listeners.push(
                AdMob.addListener(BannerAdPluginEvents.Closed, callbacks.onClosed)
            );
        }

        return {
            remove: () => listeners.forEach((l) => l.remove()),
        };
    }

    addRewardedListeners(callbacks = {}) {
        // Rewarded ads are not supported on web
        if (this.platform === 'web') {
            return { remove: () => { } };
        }

        const listeners = [];

        if (callbacks.onLoaded) {
            listeners.push(
                AdMob.addListener(RewardAdPluginEvents.Loaded, callbacks.onLoaded)
            );
        }
        if (callbacks.onFailed) {
            listeners.push(
                AdMob.addListener(RewardAdPluginEvents.FailedToLoad, callbacks.onFailed)
            );
        }
        if (callbacks.onShowed) {
            listeners.push(
                AdMob.addListener(RewardAdPluginEvents.Showed, callbacks.onShowed)
            );
        }
        if (callbacks.onDismissed) {
            listeners.push(
                AdMob.addListener(RewardAdPluginEvents.Dismissed, callbacks.onDismissed)
            );
        }
        if (callbacks.onRewarded) {
            listeners.push(
                AdMob.addListener(RewardAdPluginEvents.Rewarded, callbacks.onRewarded)
            );
        }

        return {
            remove: () => listeners.forEach((l) => l.remove()),
        };
    }
}

// Export singleton instance
export const adMobService = new AdMobService();
export default adMobService;
